// RowExpander — client-side expand/collapse for log-style table rows.
//
// Replaces the server-side `toggle_row` / `expanded_row_ids` MapSet pattern
// in dispatch_log_live and admin_events_live (ts-1 / componentize-6).
//
// Markup convention:
//   Attach phx-hook="RowExpander" to the rows container.
//   Each clickable row root: <el data-row-id="<id>">
//   Each expandable body:    <div id="row-body-<id>" class="hidden">…</div>
//
// On click, the row's `aria-expanded` flips (false ↔ true) AND the body's
// `hidden` and `show` classes flip in lockstep — `hidden` is what the
// server-rendered templates use today; `show` is the mirror class that
// downstream consumers (and accessibility tooling) read. Clicks outside
// any `[data-row-id]` are a total no-op (no pushEvent, no class flip,
// no aria mutation); a click on a row whose `#row-body-<id>` is absent
// is silent-safe.

import type { LiveViewHook } from "./types";

export interface RowExpanderState {
  _onClick: (e: Event) => void;
}

// Pure: given the current aria-expanded attr value, derive the next
// expansion state. Treats anything other than "true" as collapsed,
// matching ARIA semantics.
export const nextExpanded = (current: string | null): boolean =>
  current !== "true";

export const RowExpander = {
  mounted(this: LiveViewHook<never> & RowExpanderState) {
    const onClick = (e: Event): void => {
      const row = (e.target as HTMLElement).closest<HTMLElement>(
        "[data-row-id]",
      );
      if (!row) return;
      const rowId = row.dataset["rowId"];
      if (!rowId) return;
      const body = document.getElementById("row-body-" + rowId);
      if (!body) return;
      const expanded = nextExpanded(row.getAttribute("aria-expanded"));
      row.setAttribute("aria-expanded", expanded ? "true" : "false");
      // Server templates render `class="hidden"`; remove `hidden` and add
      // `show` to reveal, and vice versa to collapse. Both classes track
      // the same state so consumers can style off either.
      body.classList.toggle("hidden", !expanded);
      body.classList.toggle("show", expanded);
    };
    this.el.addEventListener("click", onClick);
    this._onClick = onClick;
  },

  destroyed(this: LiveViewHook<never> & RowExpanderState) {
    this.el.removeEventListener("click", this._onClick as EventListener);
  },
};
